/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { modal } from "commonAssets/lib/modal.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { validateField } from "commonAssets/lib/validateField.js";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError.js";
import { clearMessage } from "commonAssets/lib/clearMessage.js";
import { i18n, loading, reCAPTCHAv3 } from "commonAssets/js/constants";

async function validateOrderNumber(claim) {
    const orderNumber = document.getElementById("order_number");
    if (!(orderNumber instanceof HTMLInputElement)) return true;

    const value = orderNumber.value.trim();
    if (value === "") {
        toggleFieldError(orderNumber, true, i18n.getTranslation("required"));
        return false;
    }

    if (
        orderNumber.dataset.verified === "true" &&
        orderNumber.dataset.verifiedValue === value
    ) {
        return true;
    }

    const results = document.getElementById("orderNumberLookupResult");
    if (results instanceof HTMLElement) results.innerHTML = "";

    if (typeof claim?.api?.classMemberSearchV2Post !== "function") {
        modal(
            i18n.getTranslation("error"),
            "<p>Lookup is not available for this site configuration.</p>"
        );
        return false;
    }

    if (
        typeof window === "undefined" ||
        typeof window.grecaptcha === "undefined" ||
        typeof window.grecaptcha.enterprise?.execute !== "function"
    ) {
        modal(
            i18n.getTranslation("error"),
            "<p>reCAPTCHA is not available; please refresh and try again.</p>"
        );
        return false;
    }

    loadingGif(loading, "show");

    try {
        const token = await window.grecaptcha.enterprise.execute(reCAPTCHAv3, {
            action: "submit",
        });

        const lookup = {
            domain: claim?.data?.domain || getDomainFromWindow(),
            searchFields: { order_number: value },
            token,
        };

        const response = await claim.api.classMemberSearchV2Post({}, lookup);

        if (response?.data?.error) {
            const defaultMismatchError =
                "The information provided does not match our records, please try again.";
            const orderNumberHelpMessage =
                'Please try a different Order Number or contact the Settlement Administrator at <a href="mailto:Info@2025PriceSettlement.com">Info@2025PriceSettlement.com</a> for assistance in locating your Order Number.';
            const errorMessage =
                response.data.error === defaultMismatchError
                    ? orderNumberHelpMessage
                    : response.data.error;

            toggleFieldError(orderNumber, true, "");
            orderNumber.value = "";
            orderNumber.dataset.verified = "false";
            orderNumber.dataset.verifiedValue = "";

            if (results instanceof HTMLElement) {
                results.innerHTML = `<div class="alert alert-danger" role="alert">${errorMessage}</div>`;
            }
            modal(i18n.getTranslation("error"), `<p>${errorMessage}</p>`);
            return false;
        }

        const record = response?.data?.class_member || response?.data;
        if (!record || Object.keys(record).length === 0) {
            toggleFieldError(orderNumber, true, "");
            orderNumber.value = "";
            orderNumber.dataset.verified = "false";
            orderNumber.dataset.verifiedValue = "";

            const message =
                "We could not verify that Order Number. Please check the number and try again.";
            if (results instanceof HTMLElement) {
                results.innerHTML = `<div class="alert alert-warning" role="alert">${message}</div>`;
            }
            modal(i18n.getTranslation("error"), `<p>${message}</p>`);
            return false;
        }

        orderNumber.dataset.verified = "true";
        orderNumber.dataset.verifiedValue = value;
        if (results instanceof HTMLElement) {
            results.innerHTML =
                '<div class="alert alert-success" role="alert">Order Number verified.</div>';
        }
        return true;
    } catch (error) {
        console.error(error);
        modal(
            i18n.getTranslation("error"),
            "<p>There was an error processing your request, please try again.</p>"
        );
        return false;
    } finally {
        loadingGif(loading, "hide", 250);
    }
}

function getDomainFromWindow() {
    let hostname = window.location.hostname;
    if (hostname.split(".").length === 2) hostname = `www.${hostname}`;
    return hostname;
}

function setIfEmpty(id, value) {
    if (!value) return;
    const element = document.getElementById(id);
    if (!(element instanceof HTMLInputElement)) return;
    if (element.value.trim() !== "") return;
    element.value = String(value);
}

function initOrderNumberLookup(claim, onAfterPopulate = () => {}) {
    const orderNumber = document.getElementById("order_number");
    if (!(orderNumber instanceof HTMLInputElement)) return;
    if (document.getElementById("orderNumberLookupBtn")) return;

    const container =
        orderNumber.closest(".form-group") ||
        orderNumber.closest("._wrapper") ||
        orderNumber.parentElement;
    if (!(container instanceof HTMLElement)) return;

    const results = document.createElement("div");
    results.id = "orderNumberLookupResult";
    results.className = "mt-2";

    const button = document.createElement("button");
    button.type = "button";
    button.id = "orderNumberLookupBtn";
    button.className = "btn btn-primary mt-2";
    button.textContent = "Lookup Order Number";

    container.appendChild(button);
    container.appendChild(results);

    const runLookup = async () => {
        results.innerHTML = "";
        clearMessage();

        orderNumber.dataset.verified = "false";
        orderNumber.dataset.verifiedValue = "";

        const valid = validateField(orderNumber);
        if (valid === false) return;

        const value = orderNumber.value.trim();
        if (value === "") {
            toggleFieldError(orderNumber, true, i18n.getTranslation("required"));
            return;
        }

        if (typeof claim?.api?.classMemberSearchV2Post !== "function") {
            modal(
                i18n.getTranslation("error"),
                "<p>Lookup is not available for this site configuration.</p>"
            );
            return;
        }

        if (
            typeof window === "undefined" ||
            typeof window.grecaptcha === "undefined" ||
            typeof window.grecaptcha.enterprise?.execute !== "function"
        ) {
            modal(
                i18n.getTranslation("error"),
                "<p>reCAPTCHA is not available; please refresh and try again.</p>"
            );
            return;
        }

        button.disabled = true;
        loadingGif(loading, "show");

        try {
            const token = await window.grecaptcha.enterprise.execute(
                reCAPTCHAv3,
                { action: "submit" }
            );

            const lookup = {
                domain: claim?.data?.domain || getDomainFromWindow(),
                searchFields: { order_number: value },
                token,
            };

            const response = await claim.api.classMemberSearchV2Post({}, lookup);

            if (response?.data?.error) {
                const defaultMismatchError =
                    "The information provided does not match our records, please try again.";
                const orderNumberHelpMessage =
                    'Please try a different Order Number or contact the Settlement Administrator at <a href="mailto:Info@2025PriceSettlement.com">Info@2025PriceSettlement.com</a> for assistance in locating your Order Number.';
                const errorMessage =
                    response.data.error === defaultMismatchError
                        ? orderNumberHelpMessage
                        : response.data.error;

                toggleFieldError(orderNumber, true, "");
                orderNumber.value = "";
                results.innerHTML = `<div class="alert alert-danger" role="alert">${errorMessage}</div>`;
                return;
            }

            const record = response?.data?.class_member || response?.data;
            if (!record || Object.keys(record).length === 0) {
                orderNumber.value = "";
                orderNumber.dataset.verified = "false";
                orderNumber.dataset.verifiedValue = "";
                results.innerHTML =
                    '<div class="alert alert-warning" role="alert">We could not verify that Order Number. Please check the number and try again.</div>';
                return;
            }

            setIfEmpty("first_name", record.first_name);
            setIfEmpty("last_name", record.last_name);
            setIfEmpty("email_address", record.email_address);
            setIfEmpty("street_address_1", record.street_address_1);
            setIfEmpty("city", record.city);
            setIfEmpty("state", record.state);
            setIfEmpty("zip_code", record.zip_code);

            onAfterPopulate();

            orderNumber.dataset.verified = "true";
            orderNumber.dataset.verifiedValue = value;
            results.innerHTML =
                '<div class="alert alert-success" role="alert">Order Number verified.</div>';
        } catch (error) {
            console.error(error);
            modal(
                i18n.getTranslation("error"),
                "<p>There was an error processing your request, please try again.</p>"
            );
        } finally {
            loadingGif(loading, "hide", 250);
            button.disabled = false;
        }
    };

    button.addEventListener("click", runLookup);
    orderNumber.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        runLookup();
    });
    orderNumber.addEventListener("input", () => {
        orderNumber.dataset.verified = "false";
        orderNumber.dataset.verifiedValue = "";
    });
}

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    console.log("[Initiate claim]...");
    const applyDomTweaks = () => {
        document.getElementById("claimFieldsTitle")?.remove();
        document.getElementById("claimFieldsBody")?.remove();

        const paymentFieldset = document.querySelector(
            "#payment-widget > fieldset"
        );
        if (
            paymentFieldset instanceof HTMLElement &&
            !document.getElementById("paymentWidgetContactWarning")
        ) {
            const warning = document.createElement("div");
            warning.id = "paymentWidgetContactWarning";
            warning.className = "alert alert-warning mt-3";
            warning.setAttribute("role", "alert");
            warning.textContent =
                "If the mailing address, email address, or mobile phone you include with your submission becomes invalid for any reason, it is your responsibility to provide the Settlement Administrator with a current, valid mailing address, email address, and mobile phone for payment.";
            paymentFieldset.insertAdjacentElement("afterend", warning);
        }

        const firstName = document.getElementById("first_name");
        if (firstName instanceof HTMLInputElement && firstName.value.trim() !== "") {
            firstName.disabled = true;
        }

        const lastName = document.getElementById("last_name");
        if (lastName instanceof HTMLInputElement && lastName.value.trim() !== "") {
            lastName.disabled = true;
        }

        initOrderNumberLookup(claim, applyDomTweaks);
    };

    if (typeof document !== "undefined") {
        requestAnimationFrame(applyDomTweaks);
    }
    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log("[Load class member]...");
    // custom code goes here
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    console.log("[Process claim]...");
    if (typeof document === "undefined") return claim;

    const form = document.getElementById("form");
    if (form?.classList?.contains("update-email")) return claim;

    return (async () => {
        const ok = await validateOrderNumber(claim);
        if (!ok) {
            claim.flags.valid = false;
            claim.flags.suppress_error_modal = true;
        }
        return claim;
    })();
}
